import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Loader2, Save, Shield, FileText, Camera, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { platformService } from "@/services";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface ConsentSection {
  title: string;
  description: string;
  introText: string;
  items: string[];
  ctaLabel: string;
}

const DEFAULT_PRIVACY: ConsentSection = {
  title: "Privacy & Data Consent",
  description: "Please review how we handle your data before continuing.",
  introText: 'By tapping "I Consent" below, you agree to the following:',
  items: [
    "Your selfie will be processed using AI to generate a cartoon avatar.",
    "Your original photo will be stored securely and deleted after card production.",
    "Generated avatars are used solely for your personalised card design.",
    "Your facial data is not shared with third parties.",
    "You can request deletion of your data at any time.",
  ],
  ctaLabel: "I Consent",
};

const DEFAULT_TERMS: ConsentSection = {
  title: "Terms & Conditions",
  description: "Please review the terms of service before continuing.",
  introText: 'By tapping "I Accept" below, you agree to the following:',
  items: [
    "The personalised card is subject to the bank's approval process.",
    "Designs containing inappropriate, offensive, or copyrighted material will be rejected.",
    "The bank reserves the right to reject any design that does not meet brand guidelines.",
    "Card production and delivery times are estimates and may vary.",
    "Generated avatars are for personal card use only and may not be redistributed.",
    "Overage charges apply after exhausting your free generation attempts.",
    "By submitting, you confirm all provided information is accurate.",
  ],
  ctaLabel: "I Accept",
};

interface PhotoTip {
  emoji: string;
  tip: string;
}

interface PhotoSection {
  title: string;
  description: string;
  tips: PhotoTip[];
  ctaLabel: string;
}

const DEFAULT_PHOTO: PhotoSection = {
  title: "Photo Tips",
  description: "Follow these tips for the best avatar result.",
  tips: [
    { emoji: "☀️", tip: "Good lighting — face a window or use soft overhead light" },
    { emoji: "🧑", tip: "One person only — the selfie must show a single face" },
    { emoji: "😊", tip: "Neutral or smiling expression works best" },
    { emoji: "📐", tip: "Face centred and filling most of the frame" },
    { emoji: "🚫", tip: "No sunglasses, masks, or heavy filters" },
    { emoji: "📸", tip: "High resolution — avoid blurry or pixelated photos" },
  ],
  ctaLabel: "Got it — Let's Go",
};

const ConsentCopyEditor = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [privacy, setPrivacy] = useState<ConsentSection>(DEFAULT_PRIVACY);
  const [terms, setTerms] = useState<ConsentSection>(DEFAULT_TERMS);
  const [photo, setPhoto] = useState<PhotoSection>(DEFAULT_PHOTO);
  const [savedSnapshot, setSavedSnapshot] = useState("");

  const currentSnapshot = useMemo(() => JSON.stringify({ privacy, terms, photo }), [privacy, terms, photo]);
  const hasChanges = savedSnapshot !== "" && currentSnapshot !== savedSnapshot;

  useEffect(() => {
    const load = async () => {
      const [privRes, termsRes, photoRes] = await Promise.all([
        platformService.getPlatformConfig("consent_privacy"),
        platformService.getPlatformConfig("consent_terms"),
        platformService.getPlatformConfig("consent_photo"),
      ]);
      let p = DEFAULT_PRIVACY;
      let t = DEFAULT_TERMS;
      let ph = DEFAULT_PHOTO;
      if (privRes.data) {
        const val = (privRes.data as any).value;
        if (val && typeof val === "object" && val.title) p = val as ConsentSection;
      }
      if (termsRes.data) {
        const val = (termsRes.data as any).value;
        if (val && typeof val === "object" && val.title) t = val as ConsentSection;
      }
      if (photoRes.data) {
        const val = (photoRes.data as any).value;
        if (val && typeof val === "object" && val.title) ph = val as PhotoSection;
      }
      setPrivacy(p);
      setTerms(t);
      setPhoto(ph);
      setTimeout(() => setSavedSnapshot(JSON.stringify({ privacy: p, terms: t, photo: ph })), 0);
      setLoading(false);
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const results = await Promise.all([
      platformService.setPlatformConfig("consent_privacy", privacy),
      platformService.setPlatformConfig("consent_terms", terms),
      platformService.setPlatformConfig("consent_photo", photo),
    ]);
    const hasError = results.some((r) => r.error);
    if (hasError) {
      toast({ title: "Error", description: "Failed to save consent copy.", variant: "destructive" });
    } else {
      toast({ title: "Saved", description: "Consent copy updated successfully." });
      setSavedSnapshot(currentSnapshot);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const updatePrivacyItem = (index: number, value: string) => {
    const items = [...privacy.items];
    items[index] = value;
    setPrivacy({ ...privacy, items });
  };

  const updateTermsItem = (index: number, value: string) => {
    const items = [...terms.items];
    items[index] = value;
    setTerms({ ...terms, items });
  };

  const updatePhotoTip = (index: number, field: keyof PhotoTip, value: string) => {
    const tips = [...photo.tips];
    tips[index] = { ...tips[index], [field]: value };
    setPhoto({ ...photo, tips });
  };

  return (
    <div className="space-y-4">
      <Accordion type="multiple" defaultValue={["privacy", "terms", "photo"]} className="space-y-4">
        {/* Privacy Section */}
        <AccordionItem value="privacy" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <span className="font-semibold text-sm">Privacy & Data Consent</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input value={privacy.title} onChange={(e) => setPrivacy({ ...privacy, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Input value={privacy.description} onChange={(e) => setPrivacy({ ...privacy, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Intro Text</Label>
              <Input value={privacy.introText} onChange={(e) => setPrivacy({ ...privacy, introText: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Consent Points</Label>
              {privacy.items.map((item, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={item} onChange={(e) => updatePrivacyItem(i, e.target.value)} className="flex-1" />
                  <Button variant="ghost" size="icon" className="shrink-0 text-destructive" onClick={() => setPrivacy({ ...privacy, items: privacy.items.filter((_, j) => j !== i) })}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPrivacy({ ...privacy, items: [...privacy.items, ""] })}>
                <Plus className="h-3.5 w-3.5" /> Add Point
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Button Label</Label>
              <Input value={privacy.ctaLabel} onChange={(e) => setPrivacy({ ...privacy, ctaLabel: e.target.value })} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Terms Section */}
        <AccordionItem value="terms" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <span className="font-semibold text-sm">Terms & Conditions</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input value={terms.title} onChange={(e) => setTerms({ ...terms, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Input value={terms.description} onChange={(e) => setTerms({ ...terms, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Intro Text</Label>
              <Input value={terms.introText} onChange={(e) => setTerms({ ...terms, introText: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Terms Points</Label>
              {terms.items.map((item, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={item} onChange={(e) => updateTermsItem(i, e.target.value)} className="flex-1" />
                  <Button variant="ghost" size="icon" className="shrink-0 text-destructive" onClick={() => setTerms({ ...terms, items: terms.items.filter((_, j) => j !== i) })}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setTerms({ ...terms, items: [...terms.items, ""] })}>
                <Plus className="h-3.5 w-3.5" /> Add Point
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Button Label</Label>
              <Input value={terms.ctaLabel} onChange={(e) => setTerms({ ...terms, ctaLabel: e.target.value })} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Photo Tips Section */}
        <AccordionItem value="photo" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-primary" />
              <span className="font-semibold text-sm">Photo Guidelines</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input value={photo.title} onChange={(e) => setPhoto({ ...photo, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Input value={photo.description} onChange={(e) => setPhoto({ ...photo, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tips</Label>
              {photo.tips.map((tip, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={tip.emoji} onChange={(e) => updatePhotoTip(i, "emoji", e.target.value)} className="w-14 text-center shrink-0" />
                  <Input value={tip.tip} onChange={(e) => updatePhotoTip(i, "tip", e.target.value)} className="flex-1" />
                  <Button variant="ghost" size="icon" className="shrink-0 text-destructive" onClick={() => setPhoto({ ...photo, tips: photo.tips.filter((_, j) => j !== i) })}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPhoto({ ...photo, tips: [...photo.tips, { emoji: "✅", tip: "" }] })}>
                <Plus className="h-3.5 w-3.5" /> Add Tip
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Button Label</Label>
              <Input value={photo.ctaLabel} onChange={(e) => setPhoto({ ...photo, ctaLabel: e.target.value })} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Button onClick={handleSave} disabled={saving || !hasChanges} className="gap-2 disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save All Consent Copy
      </Button>
    </div>
  );
};

export default ConsentCopyEditor;

// Export types/defaults for use in ConsentFlow
export type { ConsentSection, PhotoSection, PhotoTip };
export { DEFAULT_PRIVACY, DEFAULT_TERMS, DEFAULT_PHOTO };